import { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Badge } from '@/components/ui/badge';
import { CollapsibleCard } from 'components/DesignSystems/Collapse/CollapsibleCard';
import { StylishConfirmDialog } from 'components/DesignSystems/New/StylishConfirmDialog';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import {
  useArchiveCustomEvent,
  useCustomEvent,
  type CustomEventSeverity,
  type CustomEventSummary,
} from 'hooks/customEvents/useCustomEvents';
import { parseError } from 'utils/parseTypedErrorMessage';
import { EventAssetsAtRisk } from './EventAssetsAtRisk';
import EventDetails from './EventDetails';
import { ARCHIVE_EVENT_CONFIRMATION } from './eventConfirmations';
import { EventMapButton } from './EventMapButton';
import EventResponsePlan from './EventResponsePlan';

const SEVERITY_BADGE_VARIANT = {
  low: 'green',
  medium: 'amber',
  high: 'destructive',
} as const satisfies Record<CustomEventSeverity, string>;

export const formatLabel = (value: string) =>
  value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

type EventListItemProps = {
  event: CustomEventSummary;
  defaultCollapsed?: boolean;
};

export const EventListItem: React.FC<EventListItemProps> = ({
  event,
  defaultCollapsed = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(!defaultCollapsed);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const {
    data: detail,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useCustomEvent(event.id, isExpanded);
  const archiveEvent = useArchiveCustomEvent();
  const actions = detail?.actions ?? [];

  const handleArchive = async () => {
    setShowArchiveConfirm(false);
    try {
      await archiveEvent.mutateAsync(event.id);
    } catch (error) {
      toast.error(parseError(error) ?? 'Failed to archive event.');
    }
  };

  const typeAndAors = [
    formatLabel(event.category),
    event.aors.map((aor) => aor.name).join(', '),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <CollapsibleCard
        sectionKey={`event-card-${event.id}`}
        defaultCollapsed={defaultCollapsed}
        onExpand={() => setIsExpanded(true)}
        onCollapse={() => setIsExpanded(false)}
        className="card-outlined"
        contentClassName="bg-bgdark"
        headerLeft={
          <div
            className="d-flex flex-column gap-1"
            data-testid="hub-event-item"
          >
            <span className="fw-semibold">{event.name}</span>
            <div className="d-flex gap-1">
              <Badge variant={SEVERITY_BADGE_VARIANT[event.severity]}>
                {formatLabel(event.severity)}
              </Badge>
              <Badge variant="outline">{formatLabel(event.status)}</Badge>
            </div>
            <span className="small text-neutral-400">{typeAndAors}</span>
          </div>
        }
        headerRight={
          <div
            className="d-flex align-items-center"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            {event.geometry && (
              <EventMapButton name={event.name} geometry={event.geometry} />
            )}
            <Dropdown>
              <Dropdown.Toggle
                as={StylishNewButton}
                className="button--link"
                aria-label={`${event.name} options`}
              >
                <SharedIcon iconName="more_vert" color="light" />
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                <Dropdown.Item
                  as="button"
                  disabled={archiveEvent.isPending}
                  onClick={() => setShowArchiveConfirm(true)}
                >
                  Archive
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        }
      >
        <div className="d-grid gap-3 p-3">
          <EventDetails event={event} />
          <EventResponsePlan
            eventId={event.id}
            actions={actions}
            isLoading={isDetailLoading}
            isError={isDetailError && !detail}
          />
          <EventAssetsAtRisk event={event} />
        </div>
      </CollapsibleCard>
      <StylishConfirmDialog
        show={showArchiveConfirm}
        {...ARCHIVE_EVENT_CONFIRMATION}
        onClose={() => setShowArchiveConfirm(false)}
        onConfirm={() => void handleArchive()}
      />
    </>
  );
};
